import { PERMISSIONS } from '@/constants/permissions';

import { CircleOfCareResponse } from '@/models/patients';

export enum ApiTags {
  // Batch 007 / ADR-B5.
  Patient = 'Patient',
  PatientList = 'PatientList',
  PatientPinnedStatus = 'PatientPinnedStatus',
  PatientMeasurementStatus = 'PatientMeasurementStatus',
  PatientEditDetailStatus = 'PatientEditDetailStatus',
  PatientThresholdsStatus = 'PatientThresholdsStatus',
  PatientDetailStatus = 'PatientDetailStatus',
  // Batch 011 / ADR-9 — the transfer domain on the new contract. Tags are
  // per-api-slice in RTK Query, so these live alongside the patient tags
  // without interacting with them.
  TransferList = 'TransferList',
  TransferDetail = 'TransferDetail',
  TransferConsent = 'TransferConsent',
  TransferEvents = 'TransferEvents',
}

export type ValueItem = {
  value: string | number;
  completed_time: number;
  systolic?: number;
  diastolic?: number;
};

export type AccountQueryParams = {
  page?: number;
  per?: number;
  fuzzy?: string;
  patient_group_id?: number;
  order_by?: string;
  order?: string;
  roles?: [];
};

export type AccountsResponse = {
  next: boolean;
  page: number;
  pages: number;
  users: Account[];
  per: number;
  prev: boolean;
  records: number;
};

export type Account = {
  id?: number;
  user_id?: number;
  first_name: string;
  last_name: string;
  username: string;
  cell_phone: string;
  email: string;
  account?: string;
  patient_groups?: any[];
  roles?: any[];
  is_sms_red_alarm: boolean;
};

// --- New BE (naestved v1) self-profile contract — batch 005 (me.proto) ---
// GET /v1/me response. protojson serializes responses camelCase (same as the
// login contract above). Caller-scoped: no id, no roles (GET /v1/access), no
// status/avatar (ADR-8). username is "" for SSO-only callers.
export type Me = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string; // FE-serialized "<region>-<number>", "" when unset
  smsRedAlarm: boolean;
};
// PUT /v1/me request — full editable set, PUT replace semantics. Request bodies
// use snake_case proto field names (protojson accepts them; precedent:
// auth.api.ts loginPassword sends {new_password}). Response is empty; the
// client refetches GET /v1/me. Email/username uniqueness conflicts → 409 with
// the field key in error.detail; a username change kills all previous tokens.
export type MeUpdate = {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  sms_red_alarm: boolean;
};
// POST /v1/me/password request — credential rotation, split from PUT /v1/me
// (ADR-1/4). Wrong/missing old password → 400; policy-rejected new password →
// 422. Success kills every previously issued token; the client re-logins.
export type MePasswordUpdate = {
  old_password: string;
  new_password: string;
};

export type Patients = Patient[];
export type PatientGroups = PatientGroup[];

// GET /v1/patients (batch 007 / ADR-A6).
//
// ⚠️ `size`, not `per`. Batch 006's employee list uses `per`; this contract uses
// `size`, and the two envelopes now coexist in this repo. The server default is
// 25 because the frozen rows-per-page dropdown only offers 25/50/100 and the
// footer's "1-25 of N" is computed client-side from its own rowsPerPage — a
// server default of 10 would render a 10-row table captioned "1-25 of 536".
//
// ⚠️ `status` and `organization` are ARRAYS, and they reach the wire as repeated
// keys via authedQuery's paramsSerializer. The legacy shape declared
// `patient_group?: number` while actually sending a comma-joined string — one of
// the type lies ADR-B5 removes.
//
// ⚠️ `last_alarm` and `all_measurement_is_ack` were deliberately absent until
// batch 010: their data source is patient-level severity and acknowledgement,
// which `alarm_state` now materialises. Delta 3 types them, and the note below
// records the one asymmetry worth knowing.
export type PatientsRequest = {
  page?: number;
  size?: number;
  fuzzy?: string;
  status?: string[];
  organization?: string[];
  order_by?: string;
  order?: string;
  // Batch 010 / Delta 3. `#Severity.*` slugs, and REPEATED — it reaches the wire
  // as `?last_alarm=a&last_alarm=b` through authedQuery's paramsSerializer,
  // because kratos' form decoder does not split on commas.
  last_alarm?: string[];
  // ⚠️ A three-state STRING (absent / "acknowledged" / "unacknowledged"), NOT a
  // bool — proto3 cannot distinguish an explicit false from absent. The
  // identically-named field on the RESPONSE row *is* a bool; that asymmetry is
  // deliberate and documented in the contract, so do not "fix" either side.
  all_measurement_is_ack?: string;
  // `true` restricts to the caller's own pins; any other value applies no pin
  // filter (proto3 cannot distinguish an explicit false from absent).
  pinned?: boolean;
  // The ONE global channel: `true` widens the read past the caller's own
  // bindings to the whole population. Only ever sent as `true` or omitted —
  // proto3 cannot tell an explicit `false` from absent, so a `false` on the wire
  // would be indistinguishable from "not asked for" and is dropped by the
  // endpoint's omit pass rather than serialised.
  //
  // ⚠️ The server rejects it in two combinations, so neither may be built here:
  //   · with `organization` — a chosen group and "everybody" are contradictory
  //     asks, not an intersection;
  //   · with an empty `fuzzy` — the whole population is reachable only through a
  //     deliberate search, never as a browsable list.
  all_patients?: boolean;
};

export type RolesResponse = {
  roles: Role[];
};

export type Role = {
  canonicalName: string;
  id: number;
  is_builtin_role: boolean;
  is_locked: boolean;
  name: string;
};

// GET /v1/patients/{id} — the full form payload, and also what create and update
// return (contract message PatientDetail).
//
// Declared explicitly rather than reusing the SUBMIT type, which is a different
// shape and was what the detail query used to be typed as. That mismatch is how a
// backfill reading `data.basic_info.first_name` type-checked against a response
// that has no such field: the submit type happened to declare it. Field names are
// transcribed from naestved/api/v1/patient.proto; values arrive snake_case and
// slug-free after the slice's transformResponse.
export type PatientDetail = {
  id: string;
  first_name: string;
  last_name: string;
  cpr: string;
  birth_date: string;
  email?: string;
  sex?: string;
  status?: string;
  note?: string;
  address?: string;
  postcode?: string;
  city?: string;
  admission_date?: string;
  admission_reason?: string;
  contact?: { number: string; usage?: string }[];
  relative?: CircleOfCareResponse[];
  organizations?: OrgRef[];

  // ── Batch 021's detail additions ─────────────────────────────────────────
  // The alarm read face, off the same alarm_state row and helper the list
  // projection uses. BOTH are un-slugged by patientRowFromWire (the detail query
  // runs it too), so neither reaches a renderer as `#Severity.*`.
  //
  // ⚠️ THIS SURFACE STILL RENDERS ITS BELL FROM `last_alarm`, ON PURPOSE, and it is
  // the one surface that should. The legacy detail header was fed by
  // `common-sum-info` -> `pvm.severity`, a vocabulary in which GREENCHECKED is a
  // VALUE — so a fully acknowledged patient showed a lone green tick here while the
  // list and the pinned strip showed a coloured bell with a tick (their BFF paths
  // overwrote the field with the since-last-ack severity). Aligning the three
  // surfaces is a product decision, not an equivalence fix; until it is taken, this
  // one matches the legacy by reading `last_alarm`.
  last_alarm?: string;
  latest_alarm?: string;
  all_measurement_is_ack?: boolean;
  // The real, revocable consent value, folded into the detail read.
  transfer_consent?: boolean;
  // PD-2976 — may THIS tenant still edit the consent above?
  //
  // True iff the patient arrived here BY REFERRAL: there exists an incoming
  // transfer row at ANY status (pending / accepted / rejected / ended). Consent
  // is granted on the SENDING tenant and travels with the referral, so the
  // receiving tenant has nothing to write; the server refuses PutTransferConsent
  // with 403 when this is true, and the UI hides the control rather than
  // offering a dead one.
  //
  // ⚠️ IT STAYS TRUE AFTER DISCHARGE, and that is the whole reason it exists as
  // its own field. `peer_key` above is the LIVE referral's peer and the server
  // empties it once the referral ends, so deriving the lock from `peer_key`
  // hands a discharged referred citizen an editable checkbox back — ticket row
  // 12. The two fields answer different questions; do not collapse them.
  transfer_consent_locked?: boolean;
  // The peer tenant key of the live referral, '' when none — indexes
  // feature.transfer.{targets,sources}.peers on /v1/configuration, where the
  // peer's logo lives. THE KEY, not a URL (batch 021 ADR-10).
  peer_key?: string;

  // ⚠️ NEVER SENT by this batch's contract, and declared anyway because the pages
  // still read them and must keep doing so. Each resolves to `undefined`, which is
  // the truth rather than a bug:
  //   · `thresholds`   — the read side moved to 009 in full, so the Reference
  //                      values block shows its empty state (accepted transitional
  //                      degradation, ADR-A7).
  //   · `ptf_related`  — a transfer-sink derivation, structurally always false on
  //                      this tenant; its two consumers stay untouched for 011
  //                      (ADR-A19).
  //   · `editable_cpr` — likewise a transfer derivation, owned by 011.
  // Declared as optional-and-absent rather than cast away, so the next reader sees
  // that reading them is intentional and what they will get.
  thresholds?: never[];
  ptf_related?: boolean;
  editable_cpr?: boolean;
};

// GET /v1/patients/options/statuses (batch 007 / ADR-A3).
//
// `status` holds the BARE string after the transform — see patient-slug.ts. The
// wire carries a slug and no consumer of this type ever sees one.
export type PatientStatusOption = {
  status: string;
  // May this status be CHOSEN in the edit dropdown. Its second legacy meaning —
  // the terminal-state lock on the CURRENT status — is enforced server-side.
  editable?: boolean;
  // ⚠️ An INCLUDE list: the list page seeds the true ones into its initial
  // filter. An early ADR draft had this inverted, which would have flipped the
  // opening patient set.
  is_default_filter?: boolean;
};

// Reshaped by transformResponse to the field name the existing consumers read.
export type PatientStatusOptions = {
  status: PatientStatusOption[];
};

// The shared offset-pagination envelope (rpms pkg/api/v1/page.proto), nested as
// a `page` submessage on every paged list response.
//
// ⚠️ NESTED, unlike batch 006's employee list which returns these flat as
// page/per/pages/records. Reading the flat names here yields `undefined` for
// every value, and nothing throws — the pager just renders "1-25 of 0" over a
// full table.
//
// `prev`/`next` from the legacy shape are deliberately absent: both are
// derivable from number/total_pages, and a redundant field is a second thing to
// keep correct.
export type PageResp = {
  number: number;
  size: number;
  total_records: number;
  total_pages: number;
};

export type PatientsResponse = {
  patients: Patient[];
  page: PageResp;
};

// One entry of `PatientSummary.latest_alarm_triggers` — the detail behind the
// list's "latest alarm trigger" column. WEB LIST ENVELOPE ONLY (the paged list
// and the pinned list); the mobile envelope does not carry it.
//
// Keys are snake_case here because a patient ROW crosses `snakeKeysFromWire`
// (the wire sends `latestAlarmTriggers` / `measuredAt`).
//
// Semantics, all load-bearing for the column:
//   · ONE entry per concept — the patient's most recent reading of it — and
//     present ONLY when that reading's own band is alarming.
//   · ENTRIES CAN COME FROM DIFFERENT SUBMISSIONS with different `measured_at`.
//     That is intended: a vital that has not been re-measured keeps showing, so
//     the per-entry time is the only thing that tells a nurse whether it is
//     current. It is why the time is served per entry and rendered per entry.
//   · Blood pressure arrives as TWO entries (systolic `8480-6`, diastolic
//     `8462-4`), never the legacy composite `56/78` — the kernel forbids
//     re-composing them server-side.
export type AlarmTrigger = {
  // ⚠️ `loinc` IS THE LABEL SOURCE, not `display` (PD-2985). Pass it through
  // `leafDisplayWord` / `leafDisplayUnit` and render the result via `lt()` — see
  // the patient list's alarm column, and assemble-grouped.ts for the rule.
  loinc: string;
  // The definition's own display copy — and NOT localised, whatever this comment
  // claimed until PD-2985: rpms seeds the naestved LOINC dictionary in ENGLISH,
  // so this arrives as "Heart rate / pulse" on a Danish tenant. It is also
  // single-language BY CONSTRUCTION — one column in the content — while this app
  // ships two, so it could not be the label even if the seed were Danish.
  //
  // Keep it as the FALLBACK for a loinc the app's table does not know: English
  // copy beats a raw code on screen.
  display: string;
  // The definition-side UCUM code (`mm[Hg]`, `Cel`, `/min`), NOT a display unit
  // — same story as `display`, and it reached the tooltip verbatim until
  // PD-2985. Empty for coded and text items.
  unit?: string;
  // Already RENDERED for display — a string, not a number to format.
  value: string;
  // ⚠️ A RAW `#Severity.*` SLUG, deliberately NOT un-slugged at the response
  // boundary the way the row's own `last_alarm` is: nothing renders it yet.
  // Anything that starts to must go through `severitySlugToBare` first — the
  // client's severity vocabulary is the BARE words, and comparing this against
  // one of them silently never matches (patient-slug.ts).
  //
  // As of this batch it is the LEAF's own band, not the whole submission's,
  // which is what lets the column say which vital breached.
  severity?: string;
  measured_at: string; // RFC3339 UTC
};

// One `components[]` entry of a `PatientSummary.latest_calculations` row.
// Derived from CalcComponent (declared with the /calculations endpoint below) so
// the message is described ONCE: only the multi-word key `snakeKeysFromWire`
// renames is restated here.
export type SummaryCalcComponent = Omit<CalcComponent, 'subScore'> & {
  sub_score?: number;
};

// One entry of `PatientSummary.latest_calculations` — the calculations of the
// patient's most recently scored submission. WEB LIST ENVELOPE ONLY.
//
// The SAME contract message `/v1/patients/{patient}/calculations` serves, so it
// is derived from CalcRow rather than re-declared; the four multi-word keys are
// restated in the case they arrive in, because a patient row crosses
// `snakeKeysFromWire` while the calculations slice does not.
//
// ⚠️ MATCH ON `name` (`#Calculator.TOBS` / `#Calculator.EWS`), NEVER BY
// POSITION. The array holds 0..N entries and ONE entry is a normal answer —
// "only TOBS fired" was the ordinary case in the legacy, so a missing EWS is not
// an error state.
export type SummaryCalcRow = Omit<
  CalcRow,
  'riskBand' | 'components' | 'acknowledgedAt' | 'acknowledgedBy'
> & {
  risk_band?: string;
  components?: SummaryCalcComponent[];
  acknowledged_at?: string;
  acknowledged_by?: string;
};

// A patient list row (contract message PatientSummary).
//
// ⚠️ `id` is the idp UUID, a STRING — not the detail row's surrogate primary
// key. The detail row is soft-delete-then-recreate, so its key changes; anything
// naming a person in the new system hangs off the idp.
export type Patient = {
  id: string;
  first_name: string;
  last_name: string;
  cpr: string;
  birth_date: string;
  sex: string;
  status: string;
  organizations: OrgRef[];

  // Whether the CALLING clinician has pinned this row. Replaces the legacy
  // `is_pin` plus the reverse lookup against a separate unpaged pinned query,
  // which reported already-pinned rows as unpinned once the pin count exceeded
  // one page.
  pinned: boolean;

  // ⚠️ NOT SERVED BY THIS BATCH — owned by 009 (measurements) / 010 (alarms) /
  // 011 (transfer). They stay declared, and optional, because their columns are
  // still rendered: ADR-A6 greys the three unsortable headers out rather than
  // deleting the columns, and per the cutover ruling these batches ship
  // together, so an empty cell here is never user-visible. Anything reading
  // them gets `undefined`, which is the truth.
  //
  // ⚠️ `age` IS GONE FROM THIS TYPE, deliberately (PD-2956). It was a legacy BFF
  // projection that no contract message carries, and its one reader — the
  // pinned card — wrote `age ?? '0'` and so printed `(0 years)` on every
  // citizen. A number reads as data, so unlike the dashes elsewhere in this
  // block nobody reported it for a whole release. The age is DERIVED from
  // `birth_date` at both surfaces that show it. Do not re-declare it: a field
  // declared here but never sent is this file's most expensive recurring
  // mistake (see the deleted-types note below).
  // Batch 010 / ADR-7 + Delta 3 — now SERVED. `last_measured` is an RFC3339
  // string (it was declared `number` while nothing served it, and the render
  // path still multiplied it by 1000); `last_alarm` arrives as a `#Severity.*`
  // slug and is translated to the client's bare word at the response boundary,
  // like status and sex.
  last_measured?: string;
  // The UNACKNOWLEDGED highest band, `''` when nothing is unacknowledged. Still the
  // severity FILTER's value and the sortable column key — but NOT what the bell
  // renders from; see `latest_alarm` below.
  last_alarm?: string;
  // Batch 021 / ADR-7 — the highest band IGNORING acknowledgement, `''` when never
  // measured. Paired with `all_measurement_is_ack`, this is the pair the bell is
  // drawn from, and it is the only pair that can express "had a band, all of it
  // acknowledged" — the coloured-bell-plus-tick state (alarm_state.go:72-79).
  //
  // ⚠️ RE-ADDED DELIBERATELY, and it is not the field the warning below is about.
  // The name carried a DIFFERENT meaning until batch 021 (the legacy trigger ARRAY),
  // and typing it `any` then is what let the re-key blank the page. It is typed
  // `string` here, so a third meaning would be a compile error rather than a white
  // screen. The trigger array now lives under its own name, `latest_alarm_triggers`.
  latest_alarm?: string;
  patient_groups?: PatientGroups;
  all_alarm_ack_status?: boolean;
  all_measurement_is_ack?: boolean;

  // ── Now SERVED: the two columns that rendered a literal dash ──────────────
  // ⚠️ `latest_alarm?: any` and `scores?: any` are gone AS `any`, and that is the
  // part that matters. `latest_alarm` was the legacy trigger ARRAY, then batch 021
  // re-keyed the same name to a `#Severity.*` STRING — and a string is not nullish,
  // so `?.map` did not short-circuit and every row threw inside the cell renderer.
  // `scores` was `{tobs,ews:{score,results[]}}`, a BFF projection the new contract
  // does not rebuild. Both were typed `any`, which is precisely why neither rename
  // was visible to tsc; the replacements below are typed, so the next shape change
  // is a compile error rather than a white screen.
  //
  // `latest_alarm` is now declared ABOVE as a `string` — the batch-021 meaning, the
  // one the bell reads. It is not a reinstatement of the `any`: the array meaning
  // belongs to `latest_alarm_triggers` and nothing may put a collection back on this
  // name.
  //
  // ALWAYS PRESENT on the wire — `[]` when there is nothing, never an absent
  // key. Optional here only so callers stay total (same rule as Msm's ack
  // fields): DO NOT read "absent" as a state, and DO keep the guards. An
  // unguarded property read inside a react-data-table cell renderer runs once
  // per row and takes the whole page down — this file's two worst defects were
  // both that.
  latest_alarm_triggers?: AlarmTrigger[];
  latest_calculations?: SummaryCalcRow[];

  // The peer tenant key of this patient's live referral — the institution mark
  // the list's alert column and the pinned card render, resolved against
  // /v1/configuration's peer entries (utils/transfer-peer.ts).
  //
  // SERVED since PD-2942 (rpms #158, 2026-08-28): `PatientSummary.peer_key`
  // = field 17, semantically identical to `PatientDetail.peer_key`, and all
  // three surfaces derive it from one place with no direction condition — the
  // receiving tenant used to answer `""` for every citizen it had accepted,
  // because the derivation only probed OUTGOING referrals. THE KEY, not a URL:
  // a logo is a tenant-level constant and copying it per row destroys the
  // single source of truth. The legacy `ehospitalet_logo_url` is gone.
  peer_key?: string;

  // PD-2956 — the admission columns, on the SUMMARY as well as the detail.
  //
  // These replace `patient_transfer_time` / `patient_transfer_remark`, which
  // this file itself declared "intentionally unfed": nothing ever set them, so
  // the pinned card's two rows printed a literal `-` on every tenant and every
  // status since the rebuild. Same semantics as `PatientDetail.admission_date`
  // / `admission_reason`, which are unchanged.
  //
  // ALWAYS PRESENT on the wire (the backend emits unpopulated fields), so the
  // unset form is `''` and never an absent key — do not read "absent" as a
  // state. Optional here only so callers stay total.
  //
  // `admission_date` is a plain "YYYY-MM-DD" calendar day, never timezone
  // converted, and must be parsed with DATE_FORMAT_WIRE_DAYJS: this repo
  // extends `customParseFormat`, under which parsing it as the Danish display
  // format is strict and yields an Invalid Date.
  admission_date?: string;
  admission_reason?: string;
};

// A patient's organisation membership as it appears on list and detail rows.
export type OrgRef = {
  id: string;
  name: string;
};
// ⚠️ DELETED with batch 007's gate fixes: `PinnedPatientsResponse` and
// `PatientSimpleAlarmInfo`. The pinned strip returns the contract's
// `PatientSummary`, so its rows are typed `Patient` above — the transcription of
// that message — and nothing else referenced either type.
//
// They are named here rather than silently removed because the harm was the
// declaration itself: `PatientSimpleAlarmInfo` promised `last_alarm: string`
// REQUIRED plus `birth` and `patient_groups`, none of which the contract serves.
// Typing the response with it hid every one of those reads from tsc, and the
// resulting `undefined.toLowerCase()` blanked the whole application. Do not
// reintroduce a row type that declares more than the server sends.

export type PatientGroup = {
  department_id: number;
  id: number;
  name: string;
};

// ⚠️ DELETED with PD-2956: `Metadata` and the row's `metadatas?: Metadata[]`.
//
// The legacy per-organisation EAV rows (`metadata_type` /
// `department_metadata` / `patient_metadata`), which ADR-A14 dropped as a
// capability that was never delivered — the tables held two hardcoded Danish
// keys and no type config. No response has carried them since the rebuild, and
// their last two readers (misc.utils' two extract helpers, feeding the pinned
// card) have gone with them: the admission date and the treatment indication
// are plain columns now.

export type Threshold = {};
export type Thresholds = Threshold[];

export type MfaTokenResponse = {
  temporary: string;
};

export type SendCodeRequest = MfaTokenResponse;
export type SendCodeResponse = {
  message: string;
};
export type JwtPayload = {
  accesses: PERMISSIONS[];
  aud: string;
  biz_identifier: number;
  credential: string;
  exp: number;
  iat: number;
  identifier: number;
  iss: string;
  roles: string[];
  sub: string;
  temp_reset?: 'required';
  pwd_reset?: 'required';
  destination: string;
  user_policy?: string;
  user_policy_tos_updated?: boolean;
  user_policy_pp_updated?: boolean;
  user_policy_id?: number;
  user_policy_tos_file_name?: string;
  user_policy_pp_file_name?: string;
  user_policy_tos_version?: number;
  user_policy_pp_version?: number;
  session_token?: string;
  name_id?: string;
  // SSO-session discriminator on the lean JWT (batch: SSO/SLO lean unify). The
  // lean token drops session_token/name_id (SLO material lives ufs-side keyed by
  // sub=idp); this claim is the FE's only signal that the session is an SSO one,
  // so logout routes through SLO. FE presence-checks only (value is BE-owned).
  faelles_session?: string;
};
export type AuthTokenResponse = {
  access: string;
  refresh: string;
  temporary: string;
  jwtPayload?: JwtPayload;
};

// --- New BE (naestved v1) auth contract — batch 001 (ADR-03 multistep login + /access) ---
export type LoginRequest = { username: string; password: string };
// NB: the BE serializes proto fields as camelCase JSON (protojson default), so
// multi-word fields are tmpToken / destMasked — not snake_case.
export type MfaChallenge = { destMasked: string };
// LDOC (TOS/PP) step challenge (batch 003) — legal docs pending acceptance. May
// carry only tos, only pp, or both. type ∈ {tos,pp}. publishedAt is a protojson
// int64 → serialized as a JSON string (e.g. "1784713680"), in seconds; the box
// does `publishedAt * 1000` (JS coerces the string) — do not treat it as number.
export type LegalPending = {
  type: string;
  versionId: string;
  label?: string;
  updated?: boolean;
  publishedAt?: string;
};
export type LegalChallenge = { pending: LegalPending[] };
export type LoginPending = {
  tmpToken: string;
  next: string; // "STEP_MFA" | "STEP_PWD" | "STEP_LDOC"
  mfa?: MfaChallenge;
  legal?: LegalChallenge;
};
// oneof: exactly one of completed / pending is present.
export type LoginResponse = {
  completed?: { token: string };
  pending?: LoginPending;
  // FKA org delimitation R3 — this login's FKO validation degraded. SIBLING to
  // the oneof, not inside it (proto `fko_degraded`, field 4): it is ufs's own
  // observation about the organisation registry, orthogonal to which branch
  // the login transaction took, and so is present on a pending reply too.
  //
  // The login still SUCCEEDS — bindings are frozen, nothing is deleted; the
  // flag only says the organisation set may be stale. Read it through
  // readFkoDegraded (services/fko-status.ts), never directly.
  fkoDegraded?: boolean;
};
// GET /v1/legal/documents?lang= — the logged-in legal-document read surface
// (batch 004, ADR-5). ONE endpoint carrying the storage url along with the list:
// the list is eager-fetched at bootstrap anyway (an empty list is what hides the
// sidebar entries), `lang` is known at request time, and shipping `url` inline
// saves a round trip.
//
// Field names/semantics deliberately match LegalPending above so the two surfaces
// can share render code. Note this returns the CURRENT version, not the version the
// caller signed — anyone holding a terminal token has by definition accepted
// current, so for a caller who can see the sidebar the two coincide.
export type LegalDocument = {
  // wire discriminator, "tos" | "pp"
  type: string;
  // opaque, equality-compared only
  versionId: string;
  // protojson int64 → serialized as a JSON STRING, in SECONDS. Multiply by 1000
  // before formatting; do not treat it as a number.
  publishedAt?: string;
  // storage url the browser fetches CROSS-ORIGIN (the bucket's CORS must allow it,
  // as it already does for the login-time consent gate)
  url: string;
};
// 0..2 documents. An EMPTY list is normal, not an error: a tenant with no legal
// documents configured has no consent gate either, and the FE hides the entries.
// A document whose content row is missing for `lang` is skipped by the BE.
export type LegalDocumentsResponse = {
  documents?: LegalDocument[];
};
// POST /v1/session/renew (C0 sliding renewal, ADR-05) — returns the same lean
// terminal { token } shape as login.completed, but a fresh 1h token. The FE swaps
// a business 401 (expired access) for this and replays the original request. No
// refresh; protojson camelCase single field.
export type SessionRenewResponse = {
  token: string;
};
// GET /v1/access — capability manifest. `accesses` = business-capability slugs
// (same vocabulary as legacy jwtPayload.accesses / the PERMISSIONS enum).
export type AccessManifest = {
  idp: string;
  roles: string[];
  accesses: PERMISSIONS[];
};
// --- Organization (FKO projection) — batch 016, batches/016/organization.proto ---
// One row of the local FKO mirror. Every field except timestamps is a verbatim
// FKO value: no local ids, no local vocabulary, no derived columns.
// Field names are protojson camelCase (see the NB above).
export type Org = {
  // The FKO OrganisationEnhed uuid, used verbatim as the primary key.
  id: string;
  // FKO `enhedsnavn`. NOT unique — FKO does not guarantee uniqueness.
  name: string;
  // ⚠️ BEING RETIRED, and OPTIONAL for that reason. The organisation hierarchy
  // has no consumer left: FKA org delimitation made every response row an
  // explicitly-granted unit, so the FE derives nothing from parent/child edges
  // in either direction. The rpms leg drops `parent_id` from this message
  // (proto reserved), after which the field simply stops arriving.
  //
  // The one place still reading it is toOrgOptions, to drop the top-level
  // tenant row while the field is on the wire — that read degrades to a no-op
  // by itself once it is gone. See org-options.ts.
  parentId?: string;
  // FKO `tilstand.status` verbatim ("AKTIV" / "INAKTIV"). Deliberately not
  // mapped to a local enum — an upstream enum extension must not break sync.
  status: string;
};
// GET /v1/organizations — the FULL organisation list for EVERY caller, plus
// the caller's own bindings. One response carrying both datasets on purpose:
// every consumer needs one or the other, several need both, and two endpoints
// would add a round trip and a coherence problem for nothing.
//
// How the two parts divide (FKA org delimitation R2, clarified 2026-08-12):
//
//  · `organizations` — common knowledge. An organisation's NAME is not a
//    secret; what the scope protects is the PATIENTS inside it, and that
//    protection is server-side (patient queries are clamped to the caller's
//    scope regardless of what the filter sends; out-of-scope ids are dropped
//    silently). The patient form, admin assignment/filter and the transfer
//    selects all render this full list.
//  · `bound` — the caller's bindings. Seeds the patient-list filter default
//    (ADR-11) AND scopes that one filter's OPTIONS (patient-list.tsx): they
//    are the only filter values that can match anything for a restricted
//    caller. Empty means UNSCOPED — an admin, or a caller with no bindings —
//    never "no access".
export type ListOrganizationsResponse = {
  organizations: Org[];
  bound: string[];
};
// ⚠️ DELETED with the FE-initiated org sync (FKA org delimitation):
// `OrgChange` and `SyncOrganizationsResponse`. POST /v1/organizations/sync is
// gone on both ends — the reconcile happens server-side inside the login.

// --- Employee (admin staff surface) — batch 006, batches/006/employee.proto ---
// Same serialization split as batch 005's Me/MeUpdate: responses arrive
// protojson camelCase, request bodies carry snake_case proto field names.
//
// The path key is the **idp UUID**, never the employee row's PK (ADR-1): `idp`
// is the only column employee / role / credential / through_emp2org share, and
// employee.id is an unstable surrogate "NOT referenced by anything".
//
// Roles cross this boundary as SLUGS in both directions — see constants/user.ts
// for why a numeric mapping is forbidden.

// One list row. `username` is the live PASSWORD credential's identifier and is
// an EMPTY STRING for SSO-only staff (they have no such credential).
export type EmployeeRow = {
  idp: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  roles: string[];
};

// Pagination envelope kept verbatim from the legacy contract so the list page's
// pager and URL state machine did not have to change (UI frozen).
export type ListEmployeesResponse = {
  employees: EmployeeRow[];
  page: number;
  per: number;
  pages: number;
  records: number;
  prev: boolean;
  next: boolean;
};

// GET /v1/employees query. `roles` and `organization_ids` are REPEATED proto
// fields: they must be serialized as repeated query keys, which is why
// user.api.ts builds this query string itself instead of handing arrays to
// RTK Query's `params` (those get comma-joined into a single value).
export type ListEmployeesParams = {
  page?: number;
  per?: number;
  order_by?: string;
  order?: string;
  fuzzy?: string;
  roles?: string[];
  organization_ids?: string[];
};

// The edit form's source of truth. Every field the form can submit is echoed
// here, because the write path is whole-row: a field the client fails to send
// back is written as its zero value (exactly how sms_red_alarm would get
// silently switched off — ADR-13).
export type EmployeeDetail = {
  idp: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  smsRedAlarm: boolean;
  roles: string[];
  // THIS employee's org bindings. Not obtainable from ListOrganizations, whose
  // `bound` is the CALLER's own bindings (016 organization.proto:56-58).
  organizations: string[];
  // True when the idp carries a live SAML credential. Computed by the server;
  // the client must not infer it. Drives the read-only email and username
  // rules (ADR-14).
  ssoOnly: boolean;
};

// POST /v1/employees. `organizations` is optional and may be empty: an empty
// binding set is a legal state meaning "no default patient filter", NOT "no
// access" (ADR-6, overturning the legacy required-min-1 rule).
export type EmployeeCreate = {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  cell_phone: string;
  sms_red_alarm: boolean;
  roles: string[];
  organizations: string[];
};

// PUT /v1/employees/{idp} — desired-state, whole row. The server reads current
// state inside its write transaction and computes the role/binding diff itself,
// so the client always submits the full sets (ADR-3).
export type EmployeeUpdate = EmployeeCreate & {
  idp: string;
};

// The temporary password is deliberately absent: it exists only inside the
// outbound mail. `mailDelivered: false` means the account WAS created but the
// mail did not go out — the admin must be able to tell that apart from
// "creation failed" (recovery = hand over the username, use forgot-password).
export type CreateEmployeeResponse = {
  idp: string;
  mailDelivered: boolean;
};

// Slugs only, no display names (ADR-7).
export type ListRolesResponse = {
  roles: string[];
};

// A HINT for the form, never the authority — the authority is the partial
// unique index on employee.email, whose ConstraintError surfaces as a 409 on
// write. `idp` excludes the employee being edited so their own current address
// reads as available.
export type CheckEmailAvailabilityParams = {
  email: string;
  idp?: string;
};

export type CheckEmailAvailabilityResponse = {
  available: boolean;
};

export type ExchangeMfaTokenRequest = {
  account: string;
  pwd: string;
};
export type ExchangeAuthTokenRequest = {
  temporary: string;
  code: string;
};
export type ResetPassRequest = {
  password: string;
};
export type RefreshTokenRequest = {
  refresh: string;
  access: string;
};
export type User = {
  first_name: string;
  last_name: string;
};
export type AuthState = {
  token: {
    access: string;
    // Always '' on the lean-JWT contract — there is no refresh token; an expired
    // access token is swapped via POST /v1/session/renew (authed.query.ts). Kept
    // because Token.persistToken still writes the pair.
    refresh: string;
  };
  jwtPayload?: JwtPayload;
  // capability manifest from GET /v1/access (batch 001). Permissions/roles no
  // longer ride in the lean JWT — they are fetched per session and stored here.
  accessManifest?: AccessManifest;
};

export type MeasurementRequest = {
  patient_id: number;
  from: string;
  to: string;
};

export type MeasurementResponse = {
  measurements: Questionnaire[];
};

export type GroupedMeasurementResponse = {
  measurements: {
    [measurementType: string]: NodeResult[];
  };
  scores: {
    [scoreType: string]: ScoreEWSAndTOBS[];
  };
};

export type LatestMeasurementResponse = {
  last_mea_at: number;
};

export type Questionnaire = {
  id: number;
  node_results?: NodeResult[];
  patient_id: number;
  severity: string;
};

export type NodeResult = {
  id: number;
  class: string;
  completion_time: number;
  completed_questionnaire: CompletedQuestionnaire;
  measurement_result?: MeasurementResult;
  measurement_type?: MeasurementType;
  questionnaire_node: QuestionnaireNode;
  multiple_choice_type?: string;
  result?: string;
  threshold_message?: string;
  severity: string;
};

export type ScoreEWSAndTOBS = {
  id: number;
  score: number;
  score_time: number;
  severity: string;
  is_scored: boolean;
  results: scoreEWSAndTOBSNodeResult[];
  // The acknowledger's display name. ONE slot on purpose: the wire splits the
  // name in two (`acknowledgedBy` = the local employee join, `acknowledgedByDisplay`
  // = the peer's snapshot) because the server resolves them by different routes,
  // but they are the same fact to every renderer, and this repo does not keep two
  // spellings of one semantic in a view-model (ADR-11). The assembler coalesces.
  acknowledged_by?: string;
  acknowledged_date?: number;
  // PD-2977 — the acknowledging tenant's KEY when the acknowledgement was made
  // at the other tenant, `''` otherwise. Renderers resolve copy from it with
  // `utils/transfer-peer.ts`; it is NOT itself display text.
  acknowledged_by_tenant?: string;
  // Batch 010 / ADR-6 — the real Calculation uuid, for the same reason
  // CompletedQuestionnaire.msm_id exists.
  calc_id?: string;
  // ⚠️ STILL UNFED, and PD-2977 deliberately did not change that. The field is a
  // legacy BFF URL; the new contract carries the acknowledging tenant's KEY
  // (above) and this repo's settled idiom is "name snapshotted, institution
  // LOOKED UP AT RENDER" (ADR-9 ⑧) — `transferPeerLogo(config, key)`. Feeding a
  // URL in here would force `assembleGroupedMeasurements` to take a RuntimeConfig
  // it has no other use for and copy one tenant-level constant onto every row.
  // Its two readers (node-result-value-tooltip-presentation.tsx,
  // measurement-value-cell/score-result.tsx) therefore still render no mark;
  // moving them onto the key is a separate change on surfaces PD-2977 does not
  // name.
  acknowledage_sys_logo_url?: string;
  // PD-2969 — the view-model twin of `Msm.transferredHistory`, carried on the
  // calculation row because a score is rendered in the same column as the
  // submission it scored and has to be tinted with it. A calculation carries no
  // provenance of its own on the wire; the assembler derives this from the
  // submission it joins to (`Calc.at == Msm.submittedAt`).
  //
  // ONE field where there used to be two. The pre-refactor surfaces read
  // `is_external && !is_admitted` — a pair that was never fed by anything, here
  // or on the legacy projection — and the second half of that conjunction is
  // already inside the contract's own definition: a submission is transferred
  // history only if it was imported at acceptance, which is before this tenant
  // admitted the citizen.
  is_transfer_history: boolean;
};

export type scoreEWSAndTOBSNodeResult = {
  mea_type: string;
  unit: string;
  value: number;
  is_current_submit: boolean;
  is_manual_measurement: boolean;
  complete_time: number;
  origin_source: string;
  score: number;
  severity: string;
  acknowledged_by?: string;
  acknowledged_date?: number;
};

export type MeasurementType = {
  id: number;
  name: string;
  unit: string;
};

export type MeasurementResult = {
  class: string;
  origin?: Origin;
  value: string;
  unit: string;
  severity: string;
};

export type Origin = {
  firmware_version?: string;
  hardware_version?: string;
  manufacturer?: string;
  model?: string;
  software_version?: string;
  system_id?: string;
};

export type QuestionnaireNode = {
  category: string;
  id: number;
  manual_measurement: boolean;
  measurement_type_name?: string;
};

export type CompletedQuestionnaire = {
  acknowledged_by_id: number;
  // One name slot, coalesced by the assembler from the wire's two — see the
  // note on ScoreEWSAndTOBS.acknowledged_by above.
  acknowledged_by: string;
  acknowledged_date: number;
  // PD-2977 — the acknowledging tenant's KEY, `''` for a local acknowledgement.
  acknowledged_by_tenant?: string;
  id: number;
  patient_id: number;
  questionnaire_id: number;
  // ⚠️ STILL UNFED — same decision, same reasons as the twin field on
  // ScoreEWSAndTOBS above.
  acknowledage_sys_logo_url?: string;
  // Batch 010 / ADR-6. `id` above is the assembler's NUMERIC surrogate — it only
  // has to be equal within one submission and is never sent anywhere. The ack
  // call needs the REAL row identity, which is a uuid on the new contract and
  // cannot be carried by a numeric field, so it rides alongside.
  msm_id?: string;
  // The submission instant (Msm.submittedAt) in unix seconds — the header time
  // of the submission's table column, and the value its scores align on.
  // Distinct from each leaf's completion_time, which can be backdated.
  submitted_time?: number;
  // PD-2969 — the view-model twin of `Msm.transferredHistory`; see the note on
  // the twin field on ScoreEWSAndTOBS above for why it is one field and not the
  // two the pre-refactor renderers read.
  is_transfer_history: boolean;
};

export type TransferSource = {
  patientId: number;
};

// ⚠️ DELETED with batch 011 / ADR-9: `PendingPatient`, the legacy inbox row.
// The inbox list now reads `TransferSummary` (below) off `GET /v1/transfers`.
//
// Named here rather than silently dropped, because two of its fields are the
// reason this batch had to go back to the contract:
//   · `patient_group` — the sending side's group. The frozen summary had no
//     field for it, so the column would have rendered blank; the contract
//     gained `source_organization` (the NAME, display-only) to carry it.
//   · `all_measurement_is_ack` — never set by the legacy projection either, so
//     every row read `undefined` and the alarm indicator treated it as falsy.
//     Dropping it is a no-op, NOT a lost feature. Do not reintroduce it "to be
//     safe": a declared field the server never sends is the exact shape that
//     hid three silent-undefined defects in batch 007.

// --- Batch 009 (measurement domain) wire types --------------------------------
//
// Source of truth: the FROZEN batches/009 contracts — measurement.proto (read
// side, ADR-4) and threshold.proto (resolved view + write-through override,
// ADR-3). Response JSON is protojson camelCase (same repo-wide rule as :58,
// :305-306); protojson also OMITS unset optionals and zero values, so absent
// keys are part of the contract, not an accident — hence the optional markers.
// Request bodies are snake_case (proto field names); the PUT body therefore has
// its OWN type below (PutThresholdBody) instead of reusing the response shapes
// (batch 007 lesson: one type doing response+submit duty reads empty silently).

// GET /v1/patients/{patient}/thresholds — one entry per floor loinc, cascade
// resolved (PATIENT override wins over the SYSTEM floor).
export type ResolvedThreshold = {
  loinc: string;
  display: string;
  unit: string; // UCUM, definition-side; display units stay FE constants (UI frozen)
  source: 'SYSTEM' | 'PATIENT';
  version: number;
  changeReason: string;
  // Exactly one of the two ladders is populated (kernel D-03: one band kind per
  // row); BOTH empty = capture-only (evaluates GREEN, ADR-2 list item 8).
  bands?: ThresholdBand[];
  codeBands?: ThresholdCodeBand[];
};

export type ThresholdBand = {
  severity: string; // GREEN/YELLOW/ORANGE/RED — bare word, not a slug
  high?: number; // absent = that direction does not participate (legacy null edge)
  low?: number;
};

export type ThresholdCodeBand = {
  severity: string;
  code: string; // SNOMED member code from the loinc's value_set
};

export type ResolveThresholdsResponse = {
  thresholds?: ResolvedThreshold[];
};

// PUT /v1/patients/{patient}/thresholds/{loinc} — request body, snake_case.
export type ThresholdBandInput = {
  severity: string;
  high?: number;
  low?: number;
};

export type PutThresholdBody = {
  bands: ThresholdBandInput[];
  change_reason: string;
};

export type PutThresholdResponse = {
  version?: number;
};

export type ThresholdKeyRequest = {
  patient: string;
  loinc: string;
};

export type PutThresholdRequest = ThresholdKeyRequest & {
  body: PutThresholdBody;
};

// POST /v1/measurements/ack (batch 010 / ADR-6, contract Delta 1) — request body,
// snake_case. Both lists are individually optional but not both empty; sending an
// already-acknowledged id is a server-side no-op (first-wins), and an id that does
// not exist is a BadRequest that rolls the whole batch back.
export type AckRequest = {
  measurement_ids: string[]; // Measurement uuid
  calculation_ids: string[]; // Calculation uuid
};

// Deliberately empty on the wire: the caller re-reads through the two window
// endpoints, whose rows now carry the two ack fields.
export type AckResponse = Record<string, never>;

// GET /v1/patients/{patient}/measurements?from&to — submission tree M→MN→MNF.
export type MeasurementWindowRequest = {
  patient: string;
  from: string; // RFC3339 — window predicate is submitted_at (user ruling 2)
  to: string;
};

export type ConceptCoding = {
  code: string;
  display: string;
};

// Derived from the frozen QNF ref (DEF-008-02): dictionary evolution does not
// rewrite what historic leaves read back.
export type MsmLeafConcept = {
  loinc: string;
  display?: string;
  unit?: string;
  // A coded item's value-set snapshot. ALWAYS PRESENT on the wire: a quantity
  // item carries `"valueSet":[]`, not an absent key (see the EmitUnpopulated
  // note on Msm.acknowledgedAt below).
  valueSet?: ConceptCoding[];
};

export type MsmLeaf = {
  id: string;
  measuredAt: string; // RFC3339 — payload AND the FE bucket key (rule ⑦)
  severity: string; // UNKNOWN/GREEN/YELLOW/ORANGE/RED
  concept: MsmLeafConcept;
  // oneof value — exactly one present
  quantity?: number;
  code?: string;
  text?: string;
};

export type MsmNode = {
  id: string;
  severity: string;
  fields?: MsmLeaf[];
};

export type Msm = {
  id: string;
  submittedAt: string; // RFC3339
  severity: string;
  nodes?: MsmNode[];
  // Batch 010 / ADR-10 Delta 2. RFC3339; `''` = NOT acknowledged, and
  // `acknowledged_at` is the ONLY predicate for that.
  //
  // ⚠️ ALWAYS PRESENT — the key is never absent. The kratos json codec is
  // protojson with EmitUnpopulated on, so an unacknowledged row arrives as
  // `"acknowledgedAt":""`. Confirmed against a real response body from the
  // assembled server (rpms `eb4e3c8`), which emits `"valueSet":[]` on a
  // quantity leaf and `"unit":""` on a coded leaf — both are that codec's
  // signature. The `?` survives only so callers stay total; a fixture that
  // OMITS this key is not the wire, and the presence rule is the thing it was
  // supposed to be testing.
  acknowledgedAt?: string;
  // Display name of the FIRST acknowledger (ADR-6 is first-wins). `''` when the
  // acknowledger has no live employee row, when both name fields are null, or
  // when the caller is #Role.Patient. An EMPTY DISPLAY NAME DOES NOT MEAN "not
  // acknowledged" — do not derive acked-ness from this field.
  //
  // ⚠️ IT IS THE **LOCAL** RESOLUTION, which is why PD-2977 had to add a second
  // name field rather than widen this one: a clinician who acknowledged at the
  // OTHER tenant has no employee row here and never will, so this is `''` for
  // every foreign acknowledgement.
  acknowledgedBy?: string;

  // ── PD-2977: an acknowledgement made at the OTHER tenant ──────────────────
  //
  // Carried back to the SENDING tenant and served on its Msm read face, so NK
  // can show who at the eHospital acknowledged. Both are `''` for an ordinary
  // local acknowledgement, and both follow the codec rule above — the keys are
  // present and empty rather than absent.

  // "First Last" of the acknowledging clinician at the other tenant. A SNAPSHOT
  // string: what that person was called when they acted, not a live join, for
  // the same reason the transfer log snapshots `actorDisplay` (ADR-9 ⑧).
  //
  // Name model is first + last, no middle name — settled client decision.
  acknowledgedByDisplay?: string;

  // The acknowledging tenant's KEY (e.g. `mvh`), never a display spelling.
  // ADR-3: keys cross the wire, displays do not. It indexes
  // `feature.transfer.{targets,sources}.peers` on `/v1/configuration`, and
  // `utils/transfer-peer.ts` turns it into copy — the same "name snapshotted,
  // institution looked up" split the transfer log uses.
  //
  // ⚠️ A TENANT DOES NOT IMPLY A NAME. This can hold a key while
  // `acknowledgedByDisplay` is `''` (ADR-14 §5 records exactly that on the twin
  // fields), so nothing may gate the institution on the name or vice versa.
  acknowledgedByTenant?: string;

  // PD-2969 — this submission was IMPORTED from the other tenant when the
  // referral was accepted, rather than recorded here.
  //
  // It sits on the SUBMISSION, not on the leaf, because provenance is a property
  // of the act of submitting: every reading in one submission arrived by the
  // same route. A column is one submission (2026-08-19 ruling), so a column is
  // wholly history or wholly not and no surface has to render a mixed one.
  //
  // `false` for everything recorded in this tenant, IMP hub device readings and
  // rows carried in by the legacy data migration included — neither is a
  // transfer import, and neither is tinted.
  //
  // ⚠️ ALWAYS PRESENT, `false` rather than absent, for the codec reason spelt
  // out on `acknowledgedAt` above. The `?` survives only so callers stay total;
  // a fixture that OMITS the key is not the wire.
  transferredHistory?: boolean;
};

export type FindMeasurementsResponse = {
  measurements?: Msm[]; // submitted_at DESC
};

// GET /v1/patients/{patient}/calculations?from&to
export type CalcComponent = {
  // `#CalculationParam.*` slug (pkg/constant/const.proto). Nothing reads it —
  // the factor join is on `loinc` — but fixtures must still carry the real
  // vocabulary, not an invented short word.
  param?: string;
  loinc?: string;
  subScore?: number; // a 0 sub-score arrives as 0, never absent (EmitUnpopulated)

  // ── The reading behind the sub-score (rpms `feat/calc-component-value`) ────
  // The RENDERED value of the leaf that produced this sub-score — from THE
  // MEASUREMENT THAT WAS SCORED, not the patient's most recent reading of the
  // concept. Same rendering rule as AlarmTrigger.value: shortest exact decimal,
  // the value-set display for a coded item, text verbatim.
  //
  // ⚠️ `''` is possible in principle and must not blank or throw a row.
  //
  // It is served precisely so no surface has to reconstruct it. A patient row
  // carries `latest_alarm_triggers` for the same concepts, and those are the
  // most recent reading PER CONCEPT — they may come from a different submission
  // than the one that was scored, so they are NOT a source for this value.
  value?: string;
  // From the questionnaire definition. EMPTY for coded and text items, exactly
  // like AlarmTrigger.unit — a renderer must omit the parentheses entirely
  // rather than print "()".
  unit?: string;
  // The patient-detail measurement view still reads its values off the
  // measurement LEAVES (assemble-grouped.ts rule ③): it holds the full leaf set,
  // which carries severity and the value-set snapshot too. These two fields are
  // for surfaces that hold a calculation and nothing else — the list's score
  // tooltip.
};

export type CalcRow = {
  id: string;
  name: string; // calculator slug: #Calculator.EWS / #Calculator.TOBS
  version?: string;
  total?: number; // a 0 total arrives as 0, never absent (EmitUnpopulated)
  // RiskBand slug (#RiskBand.*). EWS rows arrive as the literal
  // "RiskBandUnknown" (read side maps the stored empty string, ADR-5).
  riskBand?: string;
  at: string; // = owning Msm.submittedAt, RFC3339 — the FE join key (rule ③)
  components?: CalcComponent[];
  // Batch 010 / ADR-10 Delta 2 — same semantics as Msm's two fields above.
  //
  // ⚠️ AND DELIBERATELY NOT PD-2977's PAIR. `acknowledgedByDisplay` /
  // `acknowledgedByTenant` exist on Msm because a MEASUREMENT can be acknowledged at
  // the peer: the judgment backflow is keyed on a measurement uuid and carries nothing
  // else. A calculation never crosses, so it can only ever be acknowledged by a
  // clinician who HAS an employee row here, and `acknowledgedBy` is then the whole
  // answer. Declaring the pair here would name two fields the server has no way to
  // send — the shape `log_viewable` had when it silently decided nothing and became
  // PD-2976. If calculations are ever backflowed, add them to the wire FIRST.
  acknowledgedAt?: string;
  acknowledgedBy?: string;
};

export type FindCalculationsResponse = {
  calculations?: CalcRow[]; // at DESC
};

// GET /v1/patients/{patient}/measurements/latest
export type LatestSubmissionResponse = {
  lastSubmittedAt?: string; // RFC3339; absent/empty = no history (not a 404)
};

// --- Batch 011 (transfer domain) wire types -----------------------------------
//
// Source of truth: the FROZEN batches/011/transfer.proto (refrozen 2026-08-07,
// `8dc1a6d4346222ae`). Response JSON is protojson camelCase; REQUEST bodies are
// snake_case (proto field names) — same repo-wide rule as :58 and :305-306.
//
// ⚠️ PROVENANCE. The backend's HTTP surface is landing in parallel and no real
// response exists yet, so every shape below is transcribed from the proto, NOT
// observed on the wire. Three things must be re-derived against the first real
// response, because getting them wrong is silent:
//   ① whether unset strings arrive as "" or as absent keys. Batch 010 assumed
//      protojson omits empty values and was wrong — kratos runs EmitUnpopulated
//      and really sent `"acknowledgedAt":""`. Everything here is typed optional
//      so BOTH readings type-check, and the sentinel tests below assert on "".
//   ② `readAt` / `defaultOrganization`: "" is MEANINGFUL ("never read" / "no
//      preselect"). Never narrow these with `!== undefined` — that reads an
//      absent key as a value.
//   ③ `sourceOrganization` on an outgoing row: the contract says "", not absent.
// If the real response disagrees with any of this, that is a cross-repo
// conflict to report — not a type to quietly widen.

// One row of GET /v1/transfers, and the embedded summary inside the detail.
// `status` and `lastOption` hold the BARE legacy strings after the transform
// (see transfer-slug.ts); no consumer of this type ever sees a slug.
export type TransferSummary = {
  id: string;
  direction?: string;
  peer?: string;
  peerDisplay?: string;
  status?: string;
  lastOption?: string;
  cpr?: string;
  firstName?: string;
  lastName?: string;
  // RFC3339. Doubles as the inbox's "submission date" column (the legacy column
  // was the receiving row's creation time) and as the banner watermark input.
  createdAt?: string;
  // Preview columns, incoming only. `latestAlarm` is already the legacy UI key
  // after the transform; "" = none.
  latestAlarm?: string;
  latestTobs?: string;
  latestEws?: string;
  // The SENDING side's group NAME, display-only (proto field 14). "" on
  // outgoing rows. The uuid behind it never crosses to the FE — it travels on
  // the peer contract so the receiver can map it to a local department.
  sourceOrganization?: string;
};

// The pagination envelope AS IT ARRIVES — lowerCamel, untouched.
//
// ⚠️ Deliberately NOT `PageResp` (:227), which is the same four values in
// snake_case. That type is the POST-transform shape of the patient domain,
// where ~30 legacy render files already read snake names so `getPatients` runs
// `snakeKeysFromWire` over the envelope. The transfer domain has no such
// installed base — its readers are being rewritten by this batch — so it
// consumes the wire directly, the way batch 009's measurement types do.
//
// Reading `total_records` off this object yields `undefined` and renders
// "1-25 of 0" over a full table without throwing. That is precisely the defect
// batch 007's assembled-system gate found, which is why the two types are kept
// visibly distinct instead of being "unified".
export type TransferPage = {
  number?: number;
  size?: number;
  totalRecords?: number;
  totalPages?: number;
};

export type ListTransfersResponse = {
  transfers?: TransferSummary[];
  page?: TransferPage;
};

// GET /v1/transfers/{id}.
//
// ⚠️ There is deliberately NO `sourceOrganization` at this level. Proto field 7
// held it before 2026-08-07 and is now `reserved`, because this message embeds
// the summary and the value would otherwise appear twice. Read it from
// `transfer.sourceOrganization`. Declaring it here would type-check and read
// `undefined` forever — the "type declares more than the server sends" defect
// class that produced three silent blanks in batch 007.
export type TransferDetailResponse = {
  transfer?: TransferSummary;
  birthDate?: string;
  sex?: string;
  contactPerson?: string;
  contactNumber?: string;
  remark?: string;
  // The RECEIVER's local organization uuid, already resolved server-side
  // (source-group mapping first, then the peer's default). "" = neither is
  // configured, so there is no preselect and the operator must choose.
  defaultOrganization?: string;
  events?: TransferEvent[];
  latestEventAt?: string;
  readAt?: string; // "" = this caller has never read the ticket
  bundle?: TransferBundle;
};

// One row of the transfer history. `kind` and `option` hold BARE legacy strings
// after the transform.
export type TransferEvent = {
  id: string;
  transferId?: string;
  kind?: string;
  // 'share' | 'log' — DERIVED at the api boundary from the slug, because the
  // bare `kind` above is many-to-one and cannot express this: a consent grant
  // and a transfer acceptance both reduce to 'accept'. Not a wire field.
  logType?: string;
  // The actor's display name AS RECORDED AT THE TIME (a snapshot). There is no
  // uuid and no name split: batch 011 / ADR-9 ⑧ drops the operator uuid as an
  // accepted behaviour change (it was the peer database's local key).
  actorDisplay?: string;
  // Tenant key. The institution mark is LOOKED UP from this against the config's
  // peer list — deliberately not stored on the event, so it always shows the
  // institution's current logo. Name snapshotted, logo looked up.
  actorTenant?: string;
  option?: string;
  remark?: string;
  at?: string; // RFC3339
};

// The patient's most recently created referral. DERIVED, never a wire message —
// see utils/transfer-referral.ts, which computes it and documents why. Declared
// here beside the response it rides on so the derivation module depends on the
// types and not the other way round.
export type LatestReferral = {
  /** The referral id — what a discharge decision is POSTed against. */
  id: string;
  /**
   * The BARE kind of that referral's most recent event: 'pending' (Applied),
   * 'accept' (Accepted), 'reject' (Rejected) or 'end' (Ended).
   *
   * ⚠️ NOT the ticket-status vocabulary. `TransferSummary.status` above speaks
   * the patient-facing words ('ehospitalet' for an accepted ticket,
   * 'discharged' for an ended one); these are the event words. Naming this
   * `status` would invite a comparison against the wrong set of strings, which
   * type-checks and is silently always false.
   */
  lastEventKind: string;
  /** When the referral was created — its earliest event. RFC3339. */
  createdAt: string;
};

export type ListTransferEventsResponse = {
  events?: TransferEvent[];
  latestEventAt?: string;
  readAt?: string;
  // DERIVED at the api boundary from `events`, not a wire field — the patient's
  // most recently created referral and the kind of its latest event.
  //
  // It rides on the response so the one place that computes it is the one place
  // the stream is read. The receiving tenant's detail header needs it to decide
  // whether this patient may be discharged and against which referral, which is
  // what the legacy response's `allow_discharged` / `discharged_id` pair said.
  // See utils/transfer-referral.ts for why the browser can answer that.
  latestReferral?: LatestReferral;
};

// The measurement preview fetched live from the sending tenant. Structurally
// the 009 read surface, so `assembleGroupedMeasurements` consumes it after the
// page maps it across — display-only, never stored by the receiver.
export type TransferBundle = {
  groups?: TransferBundleGroup[];
  calcs?: TransferBundleCalc[];
};
export type TransferBundleGroup = {
  id: string; // source msm uuid — a React key here, nothing more
  submittedAt?: string;
  severity?: string;
  leaves?: TransferBundleLeaf[];
};
export type TransferBundleLeaf = {
  loinc?: string;
  measuredAt?: string;
  severity?: string;
  // proto `oneof value` — exactly one of these arrives per leaf.
  quantity?: number;
  code?: string;
  text?: string;
};
export type TransferBundleCalc = {
  name?: string;
  version?: string;
  total?: string;
  riskBand?: string;
  at?: string;
};

export type TransferConsentResponse = {
  consent?: boolean;
};

// --- request bodies (snake_case, and separate types from the responses) -------
//
// Three-way split per batch 007's lesson: response / form state / submit body
// are never the same type. A query type doing submit duty type-checks reads of
// fields the response never carries.

// POST /v1/patients/{patient}/transfers.
//
// `organization` is REQUIRED (proto field 6, uuid). It is the department the
// clinician picks on the apply form, and it genuinely reaches the other side:
// the backend validates it, then uses it to fill the peer payload's group uuid
// and name. It is NOT derived from the patient's own binding — the form seeds
// the dropdown from that binding, and whatever the clinician leaves it on is
// what ships. The value is never written back to the patient record.
export type ApplyTransferBody = {
  target?: string;
  contact_person: string;
  contact_number: string;
  remark: string;
  organization: string;
};

// POST /v1/transfers/{id}/decision. `decision` and `option` carry SLUGS — this
// is a request body, so the slug tables run outbound here.
export type DecideTransferBody = {
  decision: string;
  organization?: string; // required by the server when accepting
  option?: string; // required by the server when rejecting or ending
  remark?: string;
};

export type PutTransferConsentBody = {
  consent: boolean;
};
